import React, { useState, useEffect } from 'react';
import { aiAPI, dashboardAPI } from '../services/api';
import { FiSun, FiSearch, FiAlertTriangle, FiTrendingUp, FiZap, FiSend, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';

const AIInsights = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [insights, setInsights] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [anomalySummary, setAnomalySummary] = useState('');
  const [aiNotice, setAiNotice] = useState(''); // shown when the AI service is unavailable
  const [loading, setLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [ai, anom] = await Promise.all([
        dashboardAPI.getAIInsights().catch(() => ({ data: { insights: null } })),
        aiAPI.anomalies().catch(() => ({ data: { result: { anomalies: [] } } }))
      ]);
      if (ai.data.insights?.aiUnavailable) {
        setAiNotice(ai.data.insights.message || 'AI is unavailable');
        setInsights(null);
      } else {
        setInsights(ai.data.insights);
      }
      // Safeguard: if insights came as a string, try to parse it
      if (typeof ai.data.insights === 'string') {
        try { const parsed = JSON.parse(ai.data.insights.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')); setInsights(parsed); } catch {}
      }
      const anomalyResult = anom.data.result || anom.data;
      setAnomalies(Array.isArray(anomalyResult.anomalies) ? anomalyResult.anomalies : []);
      setAnomalySummary(anomalyResult.aiUnavailable ? '' : anomalyResult.summary || '');
      if (anomalyResult.aiUnavailable) setAiNotice(anomalyResult.message || 'AI is unavailable');
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setQuerying(true);
    try {
      const res = await aiAPI.query({ query: query.trim() });
      const result = res.data.result || {};
      setResponse(result);
      setHistory(prev => [{ query: query.trim(), response: result, time: new Date() }, ...prev].slice(0, 10));
      setQuery('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI query failed. Check OpenAI API key.');
    }
    setQuerying(false);
  };

  const sampleQueries = [
    'Show me diabetes trends in the Northeast for ages 40-60',
    'What are the top 5 diseases this quarter?',
    'Compare vaccination rates across all regions',
    'Which regions have the highest mortality rate?',
    'Identify populations at risk for chronic disease',
    'Summarize community health program effectiveness',
  ];

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>AI Analytics Engine</h1>
        <p style={{ color: '#64748b', marginTop: 4 }}>Natural language queries, predictive modeling, and anomaly detection</p>
      </div>

      {/* NL Query Box */}
      <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', borderRadius: 20, padding: 32, marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <FiZap size={24} color="#fbbf24" />
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>Ask AI About Your Data</h2>
        </div>
        <form onSubmit={handleQuery} className="ai-query-form" style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <FiSearch size={18} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Ask in natural language... e.g., 'Show diabetes rates by region'"
              style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: 12, border: '2px solid #475569', background: '#1e293b', color: '#fff', fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <button type="submit" disabled={querying}
            style={{ padding: '0 28px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 600, fontSize: 15, cursor: querying ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, opacity: querying ? 0.7 : 1 }}>
            <FiSend size={16} /> {querying ? 'Thinking...' : 'Ask AI'}
          </button>
        </form>
        <div className="sample-queries-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
          {sampleQueries.map((sq, i) => (
            <button key={i} onClick={() => setQuery(sq)}
              style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #475569', background: 'transparent', color: '#94a3b8', fontSize: 12, cursor: 'pointer', transition: 'all 0.2s' }}>
              {sq}
            </button>
          ))}
        </div>
      </div>

      {/* AI Response */}
      {response && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <FiSun size={20} color="#f59e0b" />
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>AI Response</h3>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.8, color: '#334155', whiteSpace: 'pre-wrap' }}>
            {response.explanation || 'No explanation returned.'}
          </div>
          {response.filters && Object.keys(response.filters).length > 0 && (
            <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', borderRadius: 10 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>Suggested filters{response.suggestedVisualization ? ` · best shown as a ${response.suggestedVisualization}` : ''}</h4>
              <pre style={{ fontSize: 12, color: '#475569', overflow: 'auto', maxHeight: 200 }}>{JSON.stringify(response.filters, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {aiNotice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 20, borderRadius: 12, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13 }}>
          <FiAlertTriangle size={16} /> AI analysis is currently unavailable ({aiNotice}). Figures elsewhere in the app are unaffected.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* AI Insights */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiTrendingUp size={18} color="#3b82f6" /> Trend Insights
          </h3>
          {insights ? (
            <div>
              {(insights.insights || []).map((insight, i) => (
                <div key={i} style={{ padding: '14px 16px', background: i % 2 === 0 ? '#f0f9ff' : '#f0fdf4', borderRadius: 10, marginBottom: 8, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                  <span style={{ marginRight: 8 }}>💡</span>{insight}
                </div>
              ))}
              {insights.summary && (
                <div style={{ padding: 16, background: '#fefce8', borderRadius: 10, marginTop: 12, fontSize: 13, color: '#713f12', lineHeight: 1.6 }}>
                  <strong>Executive Summary:</strong> {insights.summary}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <FiSun size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>{aiNotice ? 'AI insights unavailable' : 'No insights yet'}</p>
              <p style={{ fontSize: 12, marginTop: 4 }}>{aiNotice ? 'Try again later or contact your administrator' : 'Insights appear once there is enough data'}</p>
            </div>
          )}
        </div>

        {/* Anomaly Detection */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertTriangle size={18} color="#ef4444" /> Anomaly Detection
          </h3>
          {anomalies.length > 0 ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {anomalies.map((a, i) => (
                <div key={i} style={{ padding: '14px 16px', background: '#fef2f2', borderRadius: 10, borderLeft: '4px solid #ef4444' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#991b1b' }}>{String(a.title || a.metric || a.disease || 'Anomaly')}{a.severity ? ` · ${a.severity}` : ''}</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{String(a.description || `Value ${a.value ?? '—'}${a.expectedRange ? `, expected ${typeof a.expectedRange === 'object' ? JSON.stringify(a.expectedRange) : a.expectedRange}` : ''}`)}</div>
                  {a.date && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{String(a.date)}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <FiAlertTriangle size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>{aiNotice ? 'Anomaly detection unavailable' : 'No anomalies detected'}</p>
              <p style={{ fontSize: 12, marginTop: 4 }}>{anomalySummary || 'System monitors for unusual patterns in disease data'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Query History */}
      {history.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiClock size={18} /> Recent Queries
          </h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {history.map((h, i) => (
              <div key={i} style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, cursor: 'pointer' }} onClick={() => setQuery(h.query)}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>{h.query}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{new Date(h.time).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Predictive Models Info */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Predictive Models Available</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
          {[
            { name: 'Chronic Disease Risk', desc: 'Predicts population risk for diabetes, hypertension, heart disease', status: 'active' },
            { name: 'Outbreak Forecasting', desc: 'Forecasts communicable disease outbreaks based on trends', status: 'active' },
            { name: 'Vaccination Coverage', desc: 'Predicts coverage gaps and optimal campaign timing', status: 'active' },
            { name: 'Health Equity Analysis', desc: 'Identifies emerging disparity patterns', status: 'beta' },
            { name: 'Resource Allocation', desc: 'Optimizes program resource distribution', status: 'beta' },
            { name: 'Mortality Prediction', desc: 'Life expectancy and mortality risk modeling', status: 'planned' },
          ].map((model, i) => (
            <div key={i} style={{ padding: '16px 18px', borderRadius: 10, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{model.name}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{model.desc}</div>
              </div>
              <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, flexShrink: 0,
                background: model.status === 'active' ? '#dcfce7' : model.status === 'beta' ? '#dbeafe' : '#f1f5f9',
                color: model.status === 'active' ? '#166534' : model.status === 'beta' ? '#1e40af' : '#64748b' }}>
                {model.status.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIInsights;
